import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, mfaAPI, default as api } from '../services/api';
import tokenManager from '../services/tokenManager';
import refreshManager from '../services/refreshManager';
import { hasPermission as checkPermission, hasAnyPermission as checkAnyPermission, hasAllPermissions as checkAllPermissions } from '../utils/permissions';
import { useSnackbar } from 'notistack';
import { getUserFriendlyError } from '../utils/errorMessages';

const defaultAuthContext = {
  user: null,
  loading: false,
  loggingOut: false,
  permissions: {},
  isAuthenticated: false,
  login: async () => ({ success: false, message: 'Auth provider unavailable' }),
  register: async () => ({ success: false, message: 'Auth provider unavailable' }),
  logout: async () => {},
  updateUser: () => {},
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  isAdmin: () => false,
  canAccess: () => false
};

const AuthContext = createContext(defaultAuthContext);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [securityStatus, setSecurityStatus] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (user && user.roleData && user.roleData.permissions) {
      setPermissions(user.roleData.permissions);
    } else {
      setPermissions({});
    }
  }, [user]);

  useEffect(() => {
    const rehydrate = async () => {
      try {
        // Step 1: validate session with the backend (not just "is there a token in memory")
        const response = await authAPI.verify();
        setUser(response.data.user);
        setSubscriptionStatus(response.data.subscriptionStatus);
        // Step 2: only reset the guard AFTER the server confirms a valid session.
        // This prevents a stale in-memory token from prematurely resetting the guard.
        refreshManager.resetExpiredGuard();
      } catch (error) {
        try {
          // Access token invalid/missing — attempt to get a new one via the
          // httpOnly refresh-token cookie. This does NOT prove session validity yet.
          const refreshResponse = await authAPI.refresh();
          const newToken = refreshResponse.data?.token || refreshResponse.data?.accessToken;
          if (newToken) tokenManager.set(newToken);

          // Step 1 (retry path): validate the newly-obtained token with the backend.
          const verifyResponse = await authAPI.verify();
          setUser(verifyResponse.data.user);
          setSubscriptionStatus(verifyResponse.data.subscriptionStatus);
          // Step 2 (retry path): guard reset only after backend confirms validity.
          refreshManager.resetExpiredGuard();
        } catch (err) {
          // Check for security-related 403 codes
          const code = err.response?.data?.code;
          if (code === 'PASSWORD_EXPIRED' || code === 'FORCE_PASSWORD_RESET' || code === 'ACCOUNT_LOCKED') {
            setSecurityStatus(code);
            // Keep user partially authenticated so ProtectedRoute can redirect
            try {
              const partialUser = err.response?.data?.user || null;
              if (partialUser) setUser(partialUser);
            } catch { /* ignore */ }
          } else {
            tokenManager.clear();
            setUser(null);
            setSubscriptionStatus(null);
          }
        }
      }
      setLoading(false);
    };

    rehydrate();
  }, []);

  const login = async (email, password, forceLogoutOtherDevice = false) => {
    try {
      const response = await authAPI.login({ email, password, forceLogoutOtherDevice });
      const { user: userData, token, subscriptionStatus: subStatus, mfaRequired, mfaToken } = response.data;

      // ── MFA interception: password was valid but OTP is still needed ──
      if (mfaRequired && mfaToken) {
        return { success: false, mfaRequired: true, mfaToken };
      }

      if (token) tokenManager.set(token);
      localStorage.removeItem('token');

      setUser(userData);
      setSubscriptionStatus(subStatus);
      // Reset expiry guard so the next real expiry shows the dialog again.
      refreshManager.resetExpiredGuard();
      return { success: true, subscriptionStatus: subStatus };
    } catch (error) {
      if (error.response?.status === 402) {
        return {
          success: false,
          subscriptionExpired: true,
          message: error.response.data.message,
          subscriptionStatus: error.response.data.subscriptionStatus
        };
      }
      if (error.response?.status === 409 && error.response?.data?.activeSessionConflict) {
        return {
          success: false,
          activeSessionConflict: true,
          message: error.response.data.message
        };
      }
      const message = getUserFriendlyError(error) || 'Login failed';
      return { success: false, message };
    }
  };

  // Complete login after successful MFA OTP verification
  const loginWithMfa = async (mfaToken, code, isBackupCode = false) => {
    try {
      const response = await mfaAPI.verifyLogin({ mfaToken, code, isBackupCode });
      const { user: userData, token, subscriptionStatus: subStatus } = response.data;

      if (token) tokenManager.set(token);
      localStorage.removeItem('token');

      setUser(userData);
      setSubscriptionStatus(subStatus);
      // Reset expiry guard so the next real expiry shows the dialog again.
      refreshManager.resetExpiredGuard();
      return { success: true, subscriptionStatus: subStatus, ...response.data };
    } catch (error) {
      if (error.response?.status === 402) {
        return {
          success: false,
          subscriptionExpired: true,
          message: error.response.data?.message
        };
      }
      const message = error.response?.data?.message || getUserFriendlyError(error) || 'MFA verification failed';
      return {
        success: false,
        message,
        locked: error.response?.data?.locked,
        remainingAttempts: error.response?.data?.remainingAttempts
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { user: registeredUser, token } = response.data;
      if (token) {
        tokenManager.set(token); // store in-memory, same as login
      }
      setUser(registeredUser);
      return { success: true };
    } catch (error) {
      const message = getUserFriendlyError(error) || 'Registration failed';
      return { success: false, message };
    }
  };

  const logout = async () => {
    setLoggingOut(true);
    try {
      await api.post('/auth/logout');
    } catch (error) {
      enqueueSnackbar(getUserFriendlyError(error) || 'Failed to logout', { variant: 'error' });
    } finally {
      tokenManager.clear();
      localStorage.removeItem('token');
      setUser(null);
      setPermissions({});
      setSubscriptionStatus(null);
      setSecurityStatus(null);
      setLoggingOut(false);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const hasPermission = (permission) => {
    return checkPermission(user, permission);
  };

  const hasAnyPermission = (permissionArray) => {
    return checkAnyPermission(user, permissionArray);
  };

  const hasAllPermissions = (permissionArray) => {
    return checkAllPermissions(user, permissionArray);
  };

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.userRole?.name?.toLowerCase() === 'admin' || checkPermission(user, 'admin.user.create'));
  };

  // Maps canAccess(resource, action) → dot-notation permission key
  const RESOURCE_PERMISSION_MAP = {
    users:               { view: 'admin.user.view',                create: 'admin.user.create',               edit: 'admin.user.edit',               delete: 'admin.user.delete' },
    roles:               { view: 'admin.user.view',                create: 'admin.user.create',               edit: 'admin.user.edit',               delete: 'admin.user.delete' },
    customers:           { view: 'crm.customer.view',              create: 'crm.customer.create',             edit: 'crm.customer.edit',             delete: 'crm.customer.delete' },
    vendors:             { view: 'crm.vendor.view',                create: 'crm.vendor.create',               edit: 'crm.vendor.edit',               delete: 'crm.vendor.delete' },
    commission_agents:   { view: 'crm.commission_agent.view',      create: 'crm.commission_agent.create',     edit: 'crm.commission_agent.edit',     delete: 'crm.commission_agent.delete' },
    opportunities:       { view: 'crm.opportunity.view',           create: 'crm.opportunity.create',          edit: 'crm.opportunity.edit',          delete: 'crm.opportunity.delete' },
    tickets:             { view: 'crm.ticket.view',                create: 'crm.ticket.create',               edit: 'crm.ticket.edit',               delete: 'crm.ticket.delete' },
    activities:          { view: 'crm.activity.view',              create: 'crm.activity.create',             edit: 'crm.activity.edit',             delete: 'crm.activity.delete' },
    projects:            { view: 'crm.project.view',               create: 'crm.project.create',              edit: 'crm.project.edit',              delete: 'crm.project.delete' },
    tasks:               { view: 'crm.task.view',                  create: 'crm.task.create',                 edit: 'crm.task.edit',                 delete: 'crm.task.delete' },
    timesheets:          { view: 'crm.timesheet.view',             create: 'crm.timesheet.create',            edit: 'crm.timesheet.edit',            delete: 'crm.timesheet.delete' },
    loyalty:             { view: 'loyalty.program.view',           create: 'loyalty.program.create',          edit: 'loyalty.program.edit',          delete: 'loyalty.program.delete' },
    inventory:           { view: 'inventory.item.view',            create: 'inventory.item.create',           edit: 'inventory.item.edit',           delete: 'inventory.item.delete' },
    categories:          { view: 'inventory.category.view',        create: 'inventory.category.create',       edit: 'inventory.category.edit',       delete: 'inventory.category.delete' },
    warehouses:          { view: 'inventory.warehouse.view',       create: 'inventory.warehouse.create',      edit: 'inventory.warehouse.edit',      delete: 'inventory.warehouse.delete' },
    bins:                { view: 'inventory.bin.view',             create: 'inventory.bin.create',            edit: 'inventory.bin.edit',            delete: 'inventory.bin.delete' },
    stock_transfers:     { view: 'inventory.stock_transfer.view',  create: 'inventory.stock_transfer.create', edit: 'inventory.stock_transfer.edit', delete: 'inventory.stock_transfer.delete' },
    stock_adjustments:   { view: 'inventory.stock_adjustment.view',create: 'inventory.stock_adjustment.create',edit: 'inventory.stock_adjustment.edit',delete: 'inventory.stock_adjustment.delete' },
    cycle_counts:        { view: 'inventory.cycle_count.view',     create: 'inventory.cycle_count.create',    edit: 'inventory.cycle_count.edit',    delete: 'inventory.cycle_count.delete' },
    landed_costs:        { view: 'inventory.landed_cost.view',     create: 'inventory.landed_cost.create',    edit: 'inventory.landed_cost.edit',    delete: 'inventory.landed_cost.delete' },
    batches:             { view: 'inventory.batch.view',           create: 'inventory.batch.create',          edit: 'inventory.batch.edit',          delete: 'inventory.batch.delete' },
    shipments:           { view: 'inventory.shipment.view',        create: 'inventory.shipment.create',       edit: 'inventory.shipment.edit',       delete: 'inventory.shipment.delete' },
    units:               { view: 'inventory.unit.view',            create: 'inventory.unit.create',           edit: 'inventory.unit.edit',           delete: 'inventory.unit.delete' },
    brands:              { view: 'inventory.brand.view',           create: 'inventory.brand.create',          edit: 'inventory.brand.edit',          delete: 'inventory.brand.delete' },
    purchase_invoices:   { view: 'procurement.invoice.view',       create: 'procurement.invoice.create',      edit: 'procurement.invoice.edit',      delete: 'procurement.invoice.delete' },
    purchase_orders:     { view: 'procurement.order.view',         create: 'procurement.order.create',        edit: 'procurement.order.edit',        delete: 'procurement.order.delete' },
    debit_notes:         { view: 'procurement.debit_note.view',    create: 'procurement.debit_note.create',   edit: 'procurement.debit_note.edit',   delete: 'procurement.debit_note.delete' },
    purchase_returns:    { view: 'procurement.return.view',        create: 'procurement.return.create',       edit: 'procurement.return.edit',       delete: 'procurement.return.delete' },
    requisitions:        { view: 'procurement.requisition.view',   create: 'procurement.requisition.create',  edit: 'procurement.requisition.edit',  delete: 'procurement.requisition.delete' },
    grns:                { view: 'procurement.grn.view',           create: 'procurement.grn.create',          edit: 'procurement.grn.edit',          delete: 'procurement.grn.delete' },
    quotations:          { view: 'sales.quotation.view',           create: 'sales.quotation.create',          edit: 'sales.quotation.edit',          delete: 'sales.quotation.delete' },
    invoices:            { view: 'sales.invoice.view',             create: 'sales.invoice.create',            edit: 'sales.invoice.edit',            delete: 'sales.invoice.delete' },
    credit_notes:        { view: 'sales.credit_note.view',         create: 'sales.credit_note.create',        edit: 'sales.credit_note.edit',        delete: 'sales.credit_note.delete' },
    sales_orders:        { view: 'sales.order.view',               create: 'sales.order.create',              edit: 'sales.order.edit',              delete: 'sales.order.delete' },
    sales_returns:       { view: 'sales.return.view',              create: 'sales.return.create',             edit: 'sales.return.edit',             delete: 'sales.return.delete' },
    installment_sales:   { view: 'sales.installment.view',         create: 'sales.installment.create',        edit: 'sales.installment.edit',        delete: 'sales.installment.delete' },
    journal_entries:     { view: 'finance.journal_entry.view',     create: 'finance.journal_entry.create',    edit: 'finance.journal_entry.edit',    delete: 'finance.journal_entry.delete' },
    recurring_entries:   { view: 'finance.recurring_entry.view',   create: 'finance.recurring_entry.create',  edit: 'finance.recurring_entry.edit',  delete: 'finance.recurring_entry.delete' },
    expenses:            { view: 'finance.expense.view',           create: 'finance.expense.create',          edit: 'finance.expense.edit',          delete: 'finance.expense.delete' },
    payments:            { view: 'finance.payment.view',           create: 'finance.payment.create',          edit: 'finance.payment.edit',          delete: 'finance.payment.delete' },
    fixed_assets:        { view: 'finance.fixed_asset.view',       create: 'finance.fixed_asset.create',      edit: 'finance.fixed_asset.edit',      delete: 'finance.fixed_asset.delete' },
    budgets:             { view: 'finance.budget.view',            create: 'finance.budget.create',           edit: 'finance.budget.edit',           delete: 'finance.budget.delete' },
    employees:           { view: 'hr.employee.view',               create: 'hr.employee.create',              edit: 'hr.employee.edit',              delete: 'hr.employee.delete' },
    departments:         { view: 'hr.department.view',             create: 'hr.department.create',            edit: 'hr.department.edit',            delete: 'hr.department.delete' },
    attendance:          { view: 'hr.attendance.view',             create: 'hr.attendance.view',              edit: 'hr.attendance.view',            delete: 'hr.attendance.view' },
    payroll:             { view: 'hr.payroll.manage',              create: 'hr.payroll.manage',               edit: 'hr.payroll.manage',             delete: 'hr.payroll.manage' },
    leaves:              { view: 'hr.leave.view',                  create: 'hr.leave.create',                 edit: 'hr.leave.create',               delete: 'hr.leave.delete' },
    performance:         { view: 'hr.performance.view',            create: 'hr.performance.create',           edit: 'hr.performance.create',         delete: 'hr.performance.delete' },
    recruiting:          { view: 'hr.recruiting.view',             create: 'hr.recruiting.create',            edit: 'hr.recruiting.create',          delete: 'hr.recruiting.delete' },
    training:            { view: 'hr.training.view',               create: 'hr.training.create',              edit: 'hr.training.create',            delete: 'hr.training.delete' },
    tax_rates:           { view: 'admin.tax.view',                 create: 'admin.tax.create',                edit: 'admin.tax.edit',                delete: 'admin.tax.delete' },
    settings:            { view: 'admin.settings.manage',          create: 'admin.settings.manage',           edit: 'admin.settings.manage',         delete: 'admin.settings.manage' },
    audit_logs:          { view: 'admin.audit.view',               create: 'admin.audit.view',                edit: 'admin.audit.view',              delete: 'admin.audit.view' },
    pos:                 { view: 'pos.sale.view',                  create: 'pos.sale.create',                 edit: 'pos.sale.edit',                 delete: 'pos.sale.delete' },
    boms:                { view: 'manufacturing.bom.view',         create: 'manufacturing.bom.create',        edit: 'manufacturing.bom.edit',        delete: 'manufacturing.bom.delete' },
    production_orders:   { view: 'manufacturing.production_order.view', create: 'manufacturing.production_order.create', edit: 'manufacturing.production_order.edit', delete: 'manufacturing.production_order.delete' },
    work_centers:        { view: 'manufacturing.work_center.view', create: 'manufacturing.work_center.create', edit: 'manufacturing.work_center.edit', delete: 'manufacturing.work_center.delete' },
    ai_documents:        { view: 'ai.document.view',               create: 'ai.document.upload',              edit: 'ai.document.approve',           delete: 'ai.document.approve' },
  };

  const canAccess = (resource, action = 'view') => {
    const key = resource.toLowerCase().replace(/-/g, '_');
    const map = RESOURCE_PERMISSION_MAP[key];
    if (!map) return false;
    const perm = map[action] || map.view;
    return checkPermission(user, perm);
  };

  const value = {
    user,
    loading,
    loggingOut,
    permissions,
    subscriptionStatus,
    securityStatus,
    isAuthenticated: !!user,
    login,
    loginWithMfa,
    register,
    logout,
    updateUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    canAccess,
    allowedModules: user?.company?.allowedModules || null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
